import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Input, Component } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '@studiobuki/web-core/lib/user';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { RoutingService } from 'src/app/services/routing.service';
import { filter, map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { TRegionNameEnglish } from '@shared/jp/shipping/interfaces';
import { REGION_NAME_ENGLISH_DEFAULT } from '@shared/jp/shipping/constants';
import type { IOrderData } from '@shared/jp/interfaces';
import type { TCouponData } from '@shared/common/interfaces';
import { getDiscount } from '@shared/jp/discount/utils';
import { LIMIT_BOOKS_TO_ORDER } from '@shared/common/constants';
import { ProductionService } from 'src/app/services/production.service';
import { LoaderService } from '@studiobuki/web-core/lib/loader';
import { DiscountService } from '@studiobuki/web-core/lib/discount';
import { FirebaseService } from 'src/app/services/firebase.service';
import type { IBookCover, IBookWrapping } from '@shared/jp/data/products';
import { Logger } from '@shared/common/logger';
import type { IShipping } from '@shared/jp/data/shipping';
import {
  FEToBEProduct,
  getCalculatorItems,
  getProductByBookData,
  getProductPrice,
} from 'src/utils';
import type { TBookCover, TProductAlias } from '@shared/jp/product';
import Subscriber from '@shared/common/subscriber';
import type { IProduct } from 'src/types';
import { OrderService } from '@studiobuki/web-core/lib/order';
import type { TBookData } from '@shared/jp/models';
import { DeleteProductDialogComponent } from '../delete-product-dialog/delete-product-dialog.component';

const log = new Logger('SectionCartComponent');

@Component({
  selector: 'app-section-cart',
  templateUrl: './section-cart.component.html',
  styleUrls: ['./section-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCartComponent implements OnInit, OnDestroy {
  @Input() set region(value: TRegionNameEnglish) {
    this.region$.next(value);
  }

  public readonly region$ = new BehaviorSubject<TRegionNameEnglish>(
    REGION_NAME_ENGLISH_DEFAULT,
  );

  // public orderId$ = new ReplaySubject<IOrderData['orderId']>(1);
  public readonly order$ = new BehaviorSubject<IOrderData | undefined>(
    undefined,
  );

  public readonly products$ = this.userService.booksInCartList$.pipe(
    map((books) => {
      log.info('load: _userServiceBooksList map');

      return books.map((book) => getProductByBookData(book));
    }),
  );

  public readonly getBooksDiscount = getDiscount;

  public readonly isLimitExceeded$ = this.products$.pipe(
    map((products) => products.length > LIMIT_BOOKS_TO_ORDER),
  );

  public readonly isLoading$ = new BehaviorSubject(false);

  public readonly isProceed$ = combineLatest([
    this.products$,
    this.isLimitExceeded$,
    this.isLoading$,
  ]).pipe(
    map(
      ([products, isLimitExceeded, isLoading]) =>
        !isLoading && products.length && !isLimitExceeded,
    ),
  );

  public readonly shipping$ = this._productionService
    .getShippings$(
      combineLatest([this.products$, this.region$]).pipe(
        map(([products, region]) => ({
          booksCount: products.length || 1,
          regionName: region,
        })),
      ),
    )
    .pipe(
      map((shippings) => shippings[0]),
      filter((method): method is IShipping => !!method),
    );

  public readonly booksInOrder$ = this.products$.pipe(
    map((products) => products.map(FEToBEProduct)),
  );

  /** updated by `<app-coupon>` */
  public readonly coupon$ = new BehaviorSubject<TCouponData | undefined>(
    undefined,
  );

  public readonly calculatorItems$ = combineLatest([
    this.products$,
    this.discountService.activeDiscountCampaign$,
    this.coupon$,
    this.shipping$,
  ]).pipe(
    map(([products, discountCampaign, coupon, shipping]) =>
      getCalculatorItems(products, discountCampaign, coupon, shipping),
    ),
  );

  public coverNameMap: { [key in TBookCover]: string } = {
    hard: 'Hard Cover',
    soft: 'Soft Cover',
  };

  public readonly LIMIT_BOOKS_TO_ORDER = LIMIT_BOOKS_TO_ORDER;

  private _sub = new Subscriber();

  constructor(
    public router: Router,
    public userService: UserService<TProductAlias, TBookData>,
    public discountService: DiscountService,
    private _orderService: OrderService,
    private _firebaseService: FirebaseService,
    private _dialog: MatDialog,
    private _routing: RoutingService,
    private _loaderService: LoaderService,
    private _productionService: ProductionService,
  ) {}

  public readonly getProductPrice = getProductPrice;

  async ngOnInit() {
    const orderId = await this.createOrder();

    this._sub.push(
      this._firebaseService.getUserOrder$(orderId).subscribe((order) => {
        this.order$.next(order);
      }),
    );
  }

  ngOnDestroy() {
    this._sub.unsubscribe();
  }

  public removeProduct(product: IProduct) {
    this._dialog
      .open(DeleteProductDialogComponent, {
        data: {
          product,
        },
      })
      .afterClosed()
      .subscribe(async (result) => {
        log.info(`Dialog result: ${result}`);

        if (result) {
          await this._firebaseService.deleteUserBook(product.bookId);
        }
      });
  }

  public async selectWrapping(p: IProduct, name: IBookWrapping['name']) {
    const wrapping = p.wrappings.find((w) => w.name === name);

    if (!wrapping) {
      log.error(
        'selectWrapping - wrapping not found by name',
        name,
        'in product',
        p,
      );
      return;
    }

    await this._firebaseService.setUserBook(
      p.bookId,
      { wrapping: wrapping.id },
      { merge: true },
    );
  }

  public async selectCover(p: IProduct, id: IBookCover['id']) {
    const cover = p.covers.find((c) => c.id === id);

    if (!cover) {
      log.error('selectCover - cover not found by id', id, 'in product', p);
      return;
    }

    await this._firebaseService.setUserBook(
      p.bookId,
      { cover: id },
      { merge: true },
    );
  }

  public addAnotherBookButtonClick() {
    log.info('addAnotherBookButtonClick');
    return this._routing.goToIndex({ fragment: 'preform' });
  }

  /**
   * Navigates to order page. Make sure you do check by isProceed
   * before triggering this funciton.
   */
  async proceed() {
    this.isLoading$.next(true);

    const orderData = await firstValueFrom(this.order$);
    let orderId = orderData?.orderId;

    this._loaderService.show();

    if (orderId) {
      await this.updateOrder(orderId);
    } else {
      orderId = await this.createOrder();
    }

    this.isLoading$.next(false);

    return this.router.navigate(['order', orderId]);
  }

  /**
   * Returns orderId and creates new order if we didn`t
   * have it before
   */
  private async createOrder() {
    this.isLoading$.next(true);

    const booksInOrder = await firstValueFrom(this.booksInOrder$);
    const orderId = await this._orderService.createOrder(booksInOrder);

    this.isLoading$.next(false);

    return orderId;
  }

  private async updateOrder(orderId: IOrderData['orderId']) {
    const booksInOrder = await firstValueFrom(this.booksInOrder$);

    await this._orderService.updateOrder(orderId, { books: booksInOrder });
  }
}
